import React, { useEffect, useState } from 'react';
import {
  PiArrowClockwiseDuotone,
  PiArrowCounterClockwiseDuotone,
  PiClockCounterClockwiseDuotone,
} from 'react-icons/pi';

import { useDollhouse, useSelection } from '@spazious/dollhouse-engine';
import { useHistory, useStaging } from '@spazious/dollhouse-store';
import { Badge, ButtonProps, Stack, StackProps, ToggleButton } from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

import { useDeviceInfo } from '../../hooks/index.js';
import {
  HistoryTimeline,
  HistoryTimelineBackdrop,
  HistoryTimelineViewport,
} from '../HistoryTimeline/index.js';

export interface HistoryControlsProps extends StackProps {}

export const HistoryControls: React.FC<HistoryControlsProps> = (props) => {
  const allowTimeline = true;
  const [showTimeline, setShowTimeline] = useState(false);
  const history = useHistory();
  const invalidate = useDollhouse(({ invalidate }) => invalidate);
  const { select } = useSelection();
  const { staging } = useStaging();
  const [enabled, setEnabled] = useState(true);
  const isMobile = useDeviceInfo((state) => state.isMobile);
  const buttonSize: ButtonProps['size'] = isMobile ? 'small' : 'medium';

  const isMac = /^mac/i.test((navigator as any).userAgentData?.platform ?? navigator.platform);
  const modifierKeyPrefix = isMac ? '⌘' : 'Ctrl';

  const handleUndo = useEventCallback(() => {
    history.undo();
    select([]);
  });

  const handleRedo = useEventCallback(() => {
    history.redo();
    select([]);
  });

  const handleToggleTimeline = useEventCallback(() => {
    setShowTimeline((prev) => !prev);
  });

  useEffect(() => {
    const keydown = (e: KeyboardEvent) => {
      if (!enabled) {
        return;
      }
      // metaKey ⌘ is the command key in Mac
      const isKeyCtrlOrCommand = e.metaKey || (!isMac && e.ctrlKey);
      const isKeyZ = e.key?.toLowerCase() === 'z';

      if (isKeyCtrlOrCommand && isKeyZ) {
        e.preventDefault();
        if (e.shiftKey) {
          history.redo();
        } else {
          history.undo();
        }
        // TODO: This force the canvas to render. This will be changed with the new graph definition
        invalidate(1000);
        select([]);
      }
    };

    window.addEventListener('keydown', keydown);

    return () => {
      window.removeEventListener('keydown', keydown);
    };
  }, [history, invalidate, isMac, enabled, select]);

  useEffect(() => {
    const onStagingChange = () => {
      setEnabled(staging.stack.length === 0);
    };
    staging.on('change', onStagingChange);
    return () => {
      staging.off('change', onStagingChange);
    };
  }, [staging]);

  if (!enabled) {
    return null;
  }

  return (
    <Stack {...props}>
      <ToggleButton
        disabled={!history.hasPreviousStep}
        hotkey={`${modifierKeyPrefix} + Z`}
        size={buttonSize}
        onClick={handleUndo}
      >
        <PiArrowCounterClockwiseDuotone />
      </ToggleButton>

      <ToggleButton
        disabled={!history.hasNextStep}
        hotkey={`${modifierKeyPrefix} + ⇧ + Z`}
        size={buttonSize}
        onClick={handleRedo}
      >
        <PiArrowClockwiseDuotone />
      </ToggleButton>

      {allowTimeline && (
        <>
          <Badge count={history.size - 1} visible={history.size > 1}>
            <ToggleButton
              color={showTimeline ? 'primary' : undefined}
              disabled={!history.head}
              size={buttonSize}
              onClick={handleToggleTimeline}
            >
              <PiClockCounterClockwiseDuotone />
            </ToggleButton>
          </Badge>

          {showTimeline && (
            <HistoryTimelineBackdrop>
              <HistoryTimelineViewport>
                <HistoryTimeline />
              </HistoryTimelineViewport>
            </HistoryTimelineBackdrop>
          )}
        </>
      )}
    </Stack>
  );
};
